import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, ReverLoading } from '@itsrever/design-system'
import { UserManagement } from '@/ui/components/settings-components'
import {
  useEcommerceId,
  useProcessWarehouses,
  useSelectedEcommerceIds
} from '@/hooks'
import {
  useDashboardUsers,
  useSaveDashboardUser,
  useSetDashboardUserAccess
} from '@/application/manage-dashboard-users'
import {
  useContactsGroup,
  useEcommerceContacts
} from '@/application/manage-ecommerce-contacts'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import NotFound from '@/ui/pages/404.page'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'

export default function Users() {
  const { t } = useTranslation()
  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })
  const ecommerceId = useEcommerceId()
  const selectedIds = useSelectedEcommerceIds()
  const profile = useAppSelector((s) => s.userApi.getMe)
  const user = profile.response.user
  // getMe is dispatched from an effect, so on a direct load the role is not known yet. Deciding
  // before it lands would flash the 404 page at an admin refreshing this URL.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = user?.role === Roles.ReadWrite

  const { users, loadingUsers, usersError, reloadUsers } = useDashboardUsers({
    enabled: isAdmin,
    ecommerceId
  })
  // Required before rendering: contacts shown empty would wipe a user's topics on save.
  const contactsGroup = useContactsGroup(ecommerceId)
  const { contacts, loadingContacts, contactsError, reloadContacts } =
    useEcommerceContacts({ enabled: isAdmin, group: contactsGroup })
  const { saveUser, savingUser } = useSaveDashboardUser(ecommerceId)
  const { setUserAccess } = useSetDashboardUserAccess(ecommerceId)
  // Users and their warehouse assignments are group-wide, so no ecommerce narrowing — except
  // for REVER staff (group "*"), where the by-group lookup matches nothing and the selected
  // stores are the only scope the endpoint can answer for.
  const isEveryGroup = user?.group === '*'
  const { data: warehouses } = useProcessWarehouses(
    isEveryGroup
      ? (selectedIds ?? (ecommerceId ? [ecommerceId] : undefined))
      : undefined
  )

  const warehouseOptions = useMemo(
    () =>
      (warehouses ?? []).map((warehouse) => ({
        id: warehouse.id ?? '',
        label: warehouse.name ?? warehouse.id ?? ''
      })),
    [warehouses]
  )

  if (loadingProfile) return <ReverLoading />
  // The API refuses a non-admin caller regardless of what this page renders.
  if (!isAdmin) return <NotFound />
  if (loadingUsers || loadingContacts) return <ReverLoading />
  // An error must not render as an empty group: "No users yet" on a permissions page would
  // invite re-invites based on a list that was never loaded.
  if (usersError || contactsError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-neutral-600">
          {t('dashboard_users.load_error')}
        </p>
        <Base.Button
          type="button"
          variant="secondary"
          onClick={() => {
            if (usersError) reloadUsers()
            if (contactsError) reloadContacts()
          }}
        >
          {t('dashboard_users.retry')}
        </Base.Button>
      </div>
    )
  }

  return (
    <>
      {isFlagEnabled && <BlockedDashboardOverlay />}
      <UserManagement
        users={users}
        contacts={contacts}
        warehouses={warehouseOptions}
        currentUserEmail={user?.name}
        saving={savingUser}
        onSave={saveUser}
        onSetAccess={setUserAccess}
      />
    </>
  )
}
